// ==========================================
// Common Types
// ==========================================
export interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  error_code?: string
  detail?: unknown
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  limit: number
}

// ==========================================
// Auth Types
// ==========================================
export interface LoginRequest {
  phone: string
  password: string
  tenant_code?: string
}

export interface TenantOption {
  id: string
  name: string
  short_name: string
}

export interface LoginResponse {
  type?: 'single' | 'multi_tenant'
  // single
  access_token?: string
  refresh_token?: string
  token_type?: string
  user?: User
  tenant?: Tenant
  // multi_tenant
  session_token?: string
  tenants?: TenantOption[]
}

export interface RegisterRequest {
  email: string
  password: string
  name: string
  phone: string
}

export interface RegisterResponse {
  message: string
  user: User
}

// ==========================================
// User Types
// ==========================================
export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  TENANT_ADMIN = 'tenant_admin',
  TENANT_MANAGER = 'tenant_manager',
  DRIVER = 'driver',
}

export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
}

export interface User {
  id: string
  email?: string
  name: string
  phone: string
  role: UserRole
  status: UserStatus
  tenant_id?: string
  driver_id?: string
  created_at: string
  updated_at: string
}

// ==========================================
// Tenant Types
// ==========================================
export interface TenantCategory {
  id?: string
  name: string
  order?: number
}

export interface TenantPaymentMethod {
  id?: string
  name: string
  order?: number
}

export interface DocumentFieldConfig {
  x: number
  y: number
  font_size: number
  font_family?: string
  max_width?: number
  max_height?: number
}

export interface DocumentTemplate {
  template_path: string
  width: number
  height: number
  fields: Record<string, DocumentFieldConfig>
}

export interface DocumentTemplates {
  dispatch?: DocumentTemplate
  rental?: DocumentTemplate
}

export interface OrderFormConfig {
  hidden_fields?: string[]
}

export interface TenantFormConfig {
  order?: OrderFormConfig
  external_api_key?: string
  hide_dispatch_amount?: boolean
  show_driver_fee_info?: boolean
  show_vendors?: boolean
  vendors?: Array<{ name: string }>
}

export interface Tenant {
  id: string
  name: string
  short_name?: string
  code: string
  contact_email: string
  contact_phone: string
  tax_id?: string
  address?: string
  vehicle_categories?: TenantCategory[]
  payment_methods?: TenantPaymentMethod[]
  document_templates?: DocumentTemplates
  form_config?: TenantFormConfig
  is_active: boolean
}

// ==========================================
// Driver Types
// ==========================================
export enum DriverStatus {
  AVAILABLE = 'available',
  BUSY = 'busy',
  OFFLINE = 'offline',
  ON_BREAK = 'on_break',
}

export enum DriverApprovalStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export enum VehicleType {
  SEDAN = 'sedan',
  SUV = 'suv',
  VAN = 'van',
  MPV = 'mpv',
}

export interface Driver {
  id: string
  name: string
  phone: string
  line_user_id?: string
  vehicle_type: string
  vehicle_plate: string
  vehicle_color?: string
  vehicle_brand_model?: string
  engine_number?: string
  vehicles?: { plate: string; brand_model?: string; color?: string; type?: string }[]
  license_number: string
  license_expiry: string
  address?: string
  vehicle_inspection_date?: string
  status: DriverStatus
  approval_status: DriverApprovalStatus
  current_location?: {
    latitude: number
    longitude: number
    address?: string
  }
  tenant_id: string
  created_at: string
  updated_at: string
}

// ==========================================
// Order Types
// ==========================================
export enum OrderStatus {
  PENDING = 'pending',
  ASSIGNED = 'assigned',
  PICKING_UP = 'picking_up',
  IN_TRANSIT = 'in_transit',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  NO_SHOW = 'no_show',
}

export enum OrderType {
  NOW = 'now',
  RESERVED = 'reserved',
}

export enum PaymentType {
  CASH = 'cash',
  CREDIT_CARD = 'credit_card',
  LINE_PAY = 'line_pay',
  MONTHLY = 'monthly',
}

export interface Location {
  latitude: number
  longitude: number
  address: string
}

export interface Order {
  id: string
  order_number: string
  customer_id: string
  driver_id?: string
  status: OrderStatus
  order_type: OrderType
  pickup_address: string
  pickup_lat: number
  pickup_lng: number
  pickup_time?: string
  dropoff_address: string
  dropoff_lat: number
  dropoff_lng: number
  passenger_count: number
  vehicle_type?: string
  payment_type: PaymentType
  fare_estimate?: number
  fare_actual?: number
  note?: string
  child_seat: number
  child_seat_note?: string
  is_signed: boolean
  signed_at?: string
  dispatch_document_path?: string
  rental_document_path?: string
  documents_generated_at?: string
  tenant_id: string
  // 司機操作時間記錄
  departed_at?: string
  picked_up_at?: string
  completed_at?: string
  created_at: string
  updated_at: string
}

// ==========================================
// Customer Types
// ==========================================
export enum CustomerStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  BLACKLISTED = 'blacklisted',
}

export enum CustomerCategory {
  NORMAL = 'normal',
  VIP = 'vip',
}

export interface Customer {
  id: string
  name: string
  contact_person?: string
  phone: string
  tax_id?: string
  email?: string
  line_user_id?: string
  address?: string
  note?: string
  category: CustomerCategory
  status: CustomerStatus
  total_orders: number
  tenant_id: string
  booking_token?: string
  booking_pin?: string
  created_at: string
  updated_at: string
}

export interface CustomerCreateRequest {
  name: string
  contact_person?: string
  phone: string
  tax_id?: string
  email?: string
  line_user_id?: string
  address?: string
  note?: string
  category?: CustomerCategory
  tenant_id?: string
  booking_pin?: string
}

export interface CustomerUpdateRequest {
  name?: string
  contact_person?: string
  phone?: string
  email?: string
  line_user_id?: string
  address?: string
  note?: string
  category?: CustomerCategory
  status?: CustomerStatus
  booking_pin?: string
}

export interface CustomerPriceRule {
  id: string
  customer_id: string
  from_area: string
  to_area: string
  price: number
  vehicle_type?: string
  is_bidirectional: boolean
  note?: string
  created_at: string
  updated_at: string
}

export interface CustomerPriceRuleCreateRequest {
  from_area: string
  to_area: string
  price: number
  vehicle_type?: string
  is_bidirectional?: boolean
  note?: string
}

// ==========================================
// Dashboard Stats
// ==========================================
export interface DashboardStats {
  total_orders: number
  active_orders: number
  completed_orders: number
  total_drivers: number
  available_drivers: number
  busy_drivers: number
  total_customers: number
  today_revenue: number
  month_revenue: number
}

// ==========================================
// Sidebar Navigation
// ==========================================
export interface NavItem {
  label: string
  icon: string
  path: string
  badge?: number
  children?: NavItem[]
}

export interface NavSection {
  title?: string
  items: NavItem[]
}

// ==========================================
// AI Order Parsing Types
// ==========================================
export interface AIParseRequest {
  text: string
}

export interface PricingDetail {
  total_amount: number
  driver_amount: number
  base_fare: number
  night_surcharge: number
  cross_district_fee: number
  driver_fee: number
  vendor_rebate: number
  company_rebate: number
}

export interface AIParsedOrder {
  order_category: string
  passenger_name: string
  passenger_phone: string
  passenger_count: number
  pickup_datetime: string
  pickup_location: string
  dropoff_location: string
  additional_stops?: Array<{
    address: string
    order: number
  }>
  is_round_trip: boolean
  flight_number?: string
  vehicle_type: string
  luggage_count: number
  child_seat: number
  booster_seat: number
  name_board: boolean
  pricing: PricingDetail
  payment_method: string
  driver_notes?: string
  customer_id?: string
}

export interface AIParseResult {
  type: 'single' | 'multiple'
  orders: AIParsedOrder[]
  confidence: number
  raw_text: string
}

export interface AIParseResponse {
  success: boolean
  data?: AIParseResult
  error?: string
}
